import './Main.css'

import React, { useEffect, useMemo, useState } from 'react'
import { useWizard } from '@/common/wizard/wizard'
import { useAppContext } from '@/context/app'
import { useModal } from '@/components/modal/Modal'
import { WizardData } from './Model'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { TimeDHM } from '@/components/Elements/time'
import { secondsToDHM, toLocalISOString } from '@/common/time'
import { fetchExcel, fetchPDF } from '@/common/fetch'
import { OperatorMachineStats } from './types'
import { OperatorMachineStatsPopup } from './OperatorMachineStatsPop'
import { Order, OrderManager } from '@/components/order/Order'
import { TimeZoneNotice } from '@/components/timezone/TimeZoneNotice'

const OperatorMachineStatsOrderCols = ['username', 'maxHit', 'hitCounter', 'machines.length']
const OperatorMachineStatsDefaultOrder: Order = {
  key: 'maxHit',
  asc: false
}

export const OperatorUsageWizardResult = () => {
  const { openModal } = useModal()
  const { services, session, translates } = useAppContext()
  const { wizardData, updateWizardData, nextStep } = useWizard<WizardData>()
  const [data, setData] = useState<OperatorMachineStats[]>()
  const [pagginationState, setPagginationState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 5
  })
  const [orderManager, setOrderManager] = useState<OrderManager>(() => new OrderManager(
    OperatorMachineStatsOrderCols,
    OperatorMachineStatsDefaultOrder,
    (order: Order) => {
      setOrderManager(current => current.newManager(order))
      setPagginationState(current => ({ ...current, pageNumber: 1 }))
    }
  ))

  function refresh() {
    const params = new URLSearchParams({
      from: toLocalISOString(wizardData.from),
      to: toLocalISOString(wizardData.to),
    })
    services.session.fetchWithToken(`/api/communal/my/reports/operatorusage?${params.toString()}`).then((response) => {
      return response.json()
    }).then((data: OperatorMachineStats[]) => {
      data ||= []
      setData(data.sort((a, b) => b.maxHit - a.maxHit))
    })
  }

  function loadPDF() {
    const params = new URLSearchParams({
      from: toLocalISOString(wizardData.from),
      to: toLocalISOString(wizardData.to),
      lang: session?.user.lang || 'en'
    })
    fetchPDF({
      url: `/api/communal/my/reports/operatorusage/pdf?${params.toString()}`,
      fileName: "operatorusage.pdf",
      services
    })
  }

  function loadExcel() {
    const params = new URLSearchParams({ from: toLocalISOString(wizardData.from), to: toLocalISOString(wizardData.to), lang: session?.user.lang || 'en' })
    fetchExcel({ url: `/api/communal/my/reports/operatorusage/excel?${params.toString()}`, fileName: 'operator-usage.xlsx', services })
  }

  useEffect(refresh, [])

  const orderedData = useMemo(() => {
    return orderManager.orderListByKey(data || [])
  }, [data, orderManager])

  if (translates === undefined || wizardData === undefined || data === undefined) {
    return <div className='spinner'></div>
  }


  function headerRow(key: string, transKey?: string, sortable = false) {
    if (translates === undefined || wizardData === undefined) {
      return null
    }
    transKey = transKey ? transKey : key
    return <div className='hcol' onClick={sortable ? () => orderManager.click(key) : undefined}>
      {translates.for('report.operatorusage.summary.table.' + transKey)}
      {sortable && orderManager.orderInfoElement(key)}
    </div>
  }

  const renderTable = (
    data: OperatorMachineStats[],
    state: StaticPaginatedTableState,
    setPagginationState: React.Dispatch<React.SetStateAction<StaticPaginatedTableState>> | undefined,
    sortable = false
  ) => {
    return <StaticPaginatedTable<OperatorMachineStats>
      data={data}
      parentState={state}
      setParentState={setPagginationState}
      header={() => <>
        {headerRow('username', 'username', sortable)}
        {headerRow('maxHit', 'max_hit', sortable)}
        {headerRow('hitCounter', 'hit_counter', sortable)}
        {headerRow('machines.length', 'machine_counter', sortable)}
      </>}
      row={(row) => {
        const openUserDetails = () => {
          const url = `${window.location.origin}/communal/statistics/users?length=1d&user=${row.username}`;
          window.open(url, '_blank');
        }
        const openHitDetails = () => {
          openModal((props) => OperatorMachineStatsPopup({ ...props, data: row }))
        }
        return <>
          <div className={`col`} onClick={openUserDetails}>{row.username}</div>
          <div className={`col`} onClick={openHitDetails}>{row.maxHit.toFixed(2)}</div>
          <div className={`col`} onClick={openHitDetails}>{row.hitCounter}</div>
          <div className={`col`} onClick={openHitDetails}>{row.machines.length}</div>
        </>
      }}
      translates={translates}
    />
  }

  return <>
    <div>
      <h2>{translates.for('report.operatorusage.summary.title')}</h2>
      <TimeZoneNotice />

      <div className='right-btns right-btns-margin'>
        <button type="button" className='btn btn-next' onClick={() => {
          orderManager.setCurrentOrderKey('maxHit')
          orderManager.setDirection(false)
        }}>{translates.for('report.operatorusage.summary.best')}</button>
        <button type="button" className='btn btn-next' onClick={() => {
          orderManager.setCurrentOrderKey('maxHit')
          orderManager.setDirection(true)
        }}>{translates.for('report.operatorusage.summary.worst')}</button>
      </div>

      <h3>{translates.for('report.operatorusage.summary.all_title')}</h3>
      {renderTable(orderedData, pagginationState, setPagginationState, true)}

      <div className='right-btns right-btns-margin'>
        <button type="button" className='btn btn-next' onClick={loadExcel}>{translates.for('popup.export.excel_export')}</button>
        <button type="button" className='btn btn-next' onClick={loadPDF}>{translates.for('report.operatorusage.wizard.gen_pdf')}</button>
      </div>
    </div>
  </>
}
